import React, { useRef, useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Warning } from '@phosphor-icons/react';
import requestClient from '../components/Utility/RequestClient';

interface UseAudioRecordingOptions {
    onRecordingChange?: (isRecording: boolean) => void;
    onVolumeChange?: (volume: number) => void;
    setNewMessage: (message: string) => void;
    newMessage: string;
    i18n?: any;
    enableErrorHandling?: boolean;
}

const MAX_RECORDING_SIZE = 24.5 * 1024 * 1024;

export const useAudioRecording = ({
    onRecordingChange,
    onVolumeChange,
    setNewMessage,
    newMessage,
    i18n,
    enableErrorHandling = false,
}: UseAudioRecordingOptions) => {
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [volume, setVolume] = useState(0); // 0-255
    const mediaRecorder = useRef<MediaRecorder | null>(null);
    const chunks = useRef<Blob[]>([]);
    const audioCtx = useRef<AudioContext | null>(null);
    const analyser = useRef<AnalyserNode | null>(null);
    const dataArray = useRef<Uint8Array<ArrayBuffer> | null>(null);
    const raf = useRef<number | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const recordingSizeRef = useRef<number>(0);
    const sizeCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isAudioContextClosed = useRef<boolean>(false);
    const maxSizeToastShown = useRef<boolean>(false);
    const wakeLockRef = useRef<any>(null);

    useEffect(() => {
        if (onRecordingChange) {
            onRecordingChange(isRecording);
        }
    }, [isRecording, onRecordingChange]);

    useEffect(() => {
        if (onVolumeChange) {
            onVolumeChange(volume);
        }
    }, [volume, onVolumeChange]);

    useEffect(() => {
        return () => {
            if (sizeCheckIntervalRef.current) {
                clearInterval(sizeCheckIntervalRef.current);
            }

            if (raf.current) {
                cancelAnimationFrame(raf.current);
            }

            if (audioCtx.current && !isAudioContextClosed.current) {
                try {
                    audioCtx.current.close();
                    isAudioContextClosed.current = true;
                } catch (err) {
                    const errorMsg = i18n?.translate
                        ? i18n.translate({ id: 'error.audioContext.close', sourceName: 'Chat' })
                        : 'Error closing AudioContext:';
                    console.error(errorMsg, err);
                }
            }

            if (wakeLockRef.current) {
                wakeLockRef.current.release().catch(() => {});
                wakeLockRef.current = null;
            }
        };
    }, [i18n]);

    const requestWakeLock = useCallback(async () => {
        if ('wakeLock' in navigator) {
            try {
                wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
            } catch (err) {
                console.warn('Wake Lock konnte nicht aktiviert werden:', err);
            }
        }
    }, []);

    const releaseWakeLock = useCallback(async () => {
        if (wakeLockRef.current) {
            try {
                await wakeLockRef.current.release();
                wakeLockRef.current = null;
            } catch (err) {
                console.warn('Fehler beim Freigeben des Wake Lock:', err);
            }
        }
    }, []);

    const monitor = useCallback(() => {
        if (analyser.current && dataArray.current) {
            analyser.current.getByteFrequencyData(dataArray.current as Uint8Array<ArrayBuffer>);
            const avg = dataArray.current.reduce((a, b) => a + b, 0) / dataArray.current.length;
            setVolume(avg);
        }
        raf.current = requestAnimationFrame(monitor);
    }, []);

    const closeAudioContext = useCallback(() => {
        if (audioCtx.current && !isAudioContextClosed.current) {
            try {
                audioCtx.current.close();
                isAudioContextClosed.current = true;
            } catch (err) {
                const errorMsg = i18n?.translate
                    ? i18n.translate({ id: 'error.audioContext.close', sourceName: 'Chat' })
                    : 'Error closing AudioContext:';
                console.error(errorMsg, err);
            }
        }
    }, [i18n]);

    const stopAndSendRecording = useCallback(() => {
        releaseWakeLock();

        if (sizeCheckIntervalRef.current) {
            clearInterval(sizeCheckIntervalRef.current);
            sizeCheckIntervalRef.current = null;
        }

        if (raf.current) {
            cancelAnimationFrame(raf.current);
            raf.current = null;
        }

        closeAudioContext();

        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
        }

        if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
            mediaRecorder.current.onstop = async () => {
                setIsTranscribing(true);

                try {
                    const mimeType = mediaRecorder.current?.mimeType || 'audio/webm';
                    const cleanMimeType = mimeType.split(';')[0].trim();
                    const blob = new Blob(chunks.current, { type: cleanMimeType });

                    let fileExtension = 'webm';
                    if (cleanMimeType.includes('/')) {
                        const parts = cleanMimeType.split('/');
                        if (parts.length > 1) {
                            const subtype = parts[1].trim();
                            const extensionMap: { [key: string]: string } = {
                                webm: 'webm',
                                ogg: 'ogg',
                                mp4: 'm4a',
                                mpeg: 'mp3',
                                wav: 'wav',
                            };
                            fileExtension = extensionMap[subtype] || subtype;
                        }
                    }

                    if (blob.size === 0) {
                        throw new Error('Recording is empty');
                    }

                    const formData = new FormData();
                    formData.append('file', blob, `recording.${fileExtension}`);

                    const response = await requestClient.post('/voice/transcribe', formData);

                    if (response.status !== 200) {
                        throw new Error('Error when transcribing the audio file');
                    }

                    const data = response.data;
                    setNewMessage(newMessage + data.text);
                } catch (error) {
                    const errorMsg = i18n?.translate
                        ? i18n.translate({ id: 'toast.recording.transcriptionError', sourceName: 'Chat' })
                        : 'Error during transcription';
                    toast.error(errorMsg, {
                        duration: 5000,
                        icon: React.createElement(Warning, { size: 20 }),
                    });
                    const consoleErrorMsg = i18n?.translate
                        ? i18n.translate({ id: 'toast.recording.transcriptionError', sourceName: 'Chat' })
                        : 'Error during transcription:';
                    console.error(consoleErrorMsg, error);
                } finally {
                    setIsTranscribing(false);
                    setVolume(0);
                    setIsRecording(false);
                    recordingSizeRef.current = 0;
                    maxSizeToastShown.current = false;
                }
            };

            mediaRecorder.current.stop();
        }
    }, [closeAudioContext, releaseWakeLock, setNewMessage, newMessage, i18n]);

    const startRecording = useCallback(async () => {
        const executeRecording = async () => {
            recordingSizeRef.current = 0;
            isAudioContextClosed.current = false;
            maxSizeToastShown.current = false;

            streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });

            let options = {};
            if (MediaRecorder.isTypeSupported('audio/webm')) {
                options = { mimeType: 'audio/webm' };
            } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
                options = { mimeType: 'audio/ogg' };
            } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
                options = { mimeType: 'audio/mp4' };
            }

            mediaRecorder.current = new MediaRecorder(streamRef.current, options);
            chunks.current = [];

            audioCtx.current = new AudioContext();
            analyser.current = audioCtx.current.createAnalyser();
            analyser.current.fftSize = 256;
            dataArray.current = new Uint8Array(analyser.current.frequencyBinCount) as Uint8Array<ArrayBuffer>;
            audioCtx.current.createMediaStreamSource(streamRef.current).connect(analyser.current);
            monitor();

            mediaRecorder.current.ondataavailable = (e) => {
                if (e.data.size) {
                    chunks.current.push(e.data);
                    recordingSizeRef.current += e.data.size;

                    if (recordingSizeRef.current >= MAX_RECORDING_SIZE && !maxSizeToastShown.current) {
                        maxSizeToastShown.current = true;

                        const maxSizeMsg = i18n?.translate
                            ? i18n.translate({ id: 'toast.recording.maxSize', sourceName: 'Chat' })
                            : 'Maximum recording size reached!';
                        toast.error(maxSizeMsg, {
                            duration: 5000,
                            icon: React.createElement(Warning, { size: 20 }),
                        });

                        stopAndSendRecording();
                    }
                }
            };

            mediaRecorder.current.start(500);
            setIsRecording(true);

            await requestWakeLock();
        };

        if (enableErrorHandling) {
            try {
                await executeRecording();
            } catch (error) {
                const consoleErrorMsg = i18n?.translate
                    ? i18n.translate({ id: 'error.recording.start', sourceName: 'Chat' })
                    : 'Error starting recording:';
                console.error(consoleErrorMsg, error);
                const errorMsg = i18n?.translate
                    ? i18n.translate({ id: 'toast.recording.microphoneDenied', sourceName: 'Chat' })
                    : 'Microphone access denied';
                toast.error(errorMsg, {
                    duration: 5000,
                    icon: React.createElement(Warning, { size: 20 }),
                });
            }
        } else {
            await executeRecording();
        }
    }, [enableErrorHandling, i18n, monitor, requestWakeLock, stopAndSendRecording]);

    const cancelRecording = useCallback(() => {
        releaseWakeLock();

        if (sizeCheckIntervalRef.current) {
            clearInterval(sizeCheckIntervalRef.current);
            sizeCheckIntervalRef.current = null;
        }

        if (raf.current) {
            cancelAnimationFrame(raf.current);
            raf.current = null;
        }

        if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
            mediaRecorder.current.stop();
        }

        closeAudioContext();

        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
        }

        setIsRecording(false);
        recordingSizeRef.current = 0;
        maxSizeToastShown.current = false;
    }, [closeAudioContext, releaseWakeLock]);

    return {
        isRecording,
        isTranscribing,
        volume,
        startRecording,
        cancelRecording,
        stopAndSendRecording,
    };
};
